/**
 * @see https://umijs.org/zh-CN/plugins/plugin-access
 * */
export default function access(initialState: { currentUser?: USERAPI.CurrentUser; checkGreyAccess?: (permissionName: string) => boolean } | undefined) {
  const { currentUser, checkGreyAccess = () => false  } = initialState ?? {};

  const checkAccess = (permissionName: string) => {
    const { permissions = [] } = currentUser?.role || {};
    const _departments = permissions?.map((item: { name: string }) => {
      return item.name;
    });
    return _departments.includes(permissionName);
  };

  const permissions = {
    // 员工管理菜单权限
    'menu.user': checkAccess('user-accounts') || checkAccess('role-permissions'),
    // 仪表盘
    dashboard: checkAccess('dashboard'),
    // 账号管理
    'user-accounts': checkAccess('user-accounts'),
    // 组织架构编辑
    'edit-departments': checkAccess(
      'POST:api/admin/departments|PUT:api/admin/departments/{department}|DELETE:api/admin/departments/{department}',
    ),
    // 新增员工
    'create-user': checkAccess('POST:api/admin/users'),
    // 导入员工
    'import-user': checkAccess('POST:api/admin/users/import'),
    // 编辑员工
    'edit-user': checkAccess('PUT:api/admin/users/{user}'),
    // 删除员工
    'delete-user': checkAccess('DELETE:api/admin/users/{user}'),

    // 角色权限
    'role-permissions': checkAccess('role-permissions'),
    // 新增角色
    'create-role': checkAccess('POST:api/admin/role-permissions'),
    // 权限设置
    'edit-role': checkAccess('PUT:api/admin/role-permissions/{role}'),
    // 删除权限
    'delete-role': checkAccess('DELETE:api/admin/role-permissions/{role}'),

    // NOTE 预设话术
    'knowledge': checkAccess('canned') || checkAccess('email-templates'),
    // 话术菜单
    'preset-script': checkAccess('canned'),
    // 话术分组，增删改查
    'preset-script-folder': checkAccess(
      'POST:api/admin/canned-response/folders|PUT:api/admin/canned-response/folders/{id}|DELETE:api/admin/canned-response/folders',
    ),
    // 话术列表，新增、编辑、查询
    'preset-script-list': checkAccess('canned-responses'),
    // 新增/编辑 - 个人模板
    'preset-script-private-template': checkAccess('canned-responses-private'),
    // 新增/编辑 - 公共模板
    'preset-script-public-template': checkAccess('canned-responses-public'),
    // 话术删除
    'preset-script-list-delete': checkAccess('DELETE:api/admin/canned-response/responses'),

    // 回复模板管理
    'email-templates': checkAccess('email-templates'),

    // 争议工单菜单
    'chat-ticket': checkAccess('chat-ticket'),
    // 工单指派
    'dispute-assign': checkAccess('POST:api/admin/chat-ticket/assign'),
    'dispute-close': checkAccess('PUT:api/admin/saas-dispute/set-status-close'),
    // 单个工单指派
    'single-dispute-assign': checkAccess('POST:api/admin/chat-ticket/{ticket}/assign'),

    /**
     * NOTE 邮件工单
     */
    // CARE工单菜单
    // 'care-email-ticket': checkGreyAccess('CARE_EMAIL'),
    'care-email-ticket': checkAccess('batch-send'),
    // 批量发送
    'care-batch-send': checkAccess('proactive-outreach-center-batch-send'),
    // 批量发送（技术）
    'care-batch-send-tech': checkAccess('proactive-outreach-center-batch-send-tech'),

    // 邮件工单菜单
    'email-ticket': checkAccess('email-ticket'),
    // 邮件工单指派（单个）
    'email-ticket-assign': checkAccess('POST:api/admin/email-ticket/user-for-single'),
    // 邮件工单批量指派
    'email-ticket-batch-assign': checkAccess('POST:api/admin/email-ticket/user'),
    // 邮件工单批量更新
    'email-ticket-batch-update': checkAccess(
      'POST:api/admin/email-ticket/update|POST:api/admin/email-ticket/tag',
    ),
    // 邮件工单批量关闭
    'email-ticket-batch-close': checkAccess('POST:api/admin/email-ticket/close'),
    // 工单可多选
    'email-ticket-multi-select':
      checkAccess('POST:api/admin/email-ticket/user') ||
      checkAccess('POST:api/admin/email-ticket/update|POST:api/admin/email-ticket/tag') ||
      checkAccess('POST:api/admin/email-ticket/close'),

    // 导出工单列表
    'export-list': checkAccess('POST:api/admin/export-task/list'),

    // 导出Saas工单
    'create-saas-export-task': checkAccess('POST:api/admin/saas-dispute/export'),
    // 导出Email工单
    'create-email-export-task': checkAccess('POST:api/admin/email-ticket/export'),
    // 邮件详情查看订单列表
    'email-ticket-order-list': checkAccess('POST:api/admin/email-ticket/order-center'),

    // 系统设置
    system: checkAccess('auto-rule') || checkAccess('email-management') || checkAccess('tag'),
    // 自动化规则
    'auto-rule': checkAccess('auto-rule'),
    'email-management': checkAccess('email-management'),
    tag: checkAccess('tag'),
    // oa流程相关
    // 提交OA流程
    'oa-process': checkAccess('oa-process-submit'),
    // 提交OA流程-梯度优惠
    'oa-process-gradient': checkAccess('ladder-coupons'),
    // 提交OA流程-其他退款
    'oa-process-refund': checkAccess('other-refund'),
    // 提交OA流程-取消订单
    'oa-process-cancel': checkAccess('cancel-order'),
    // 提交OA流程-物流核实
    'oa-process-logistics': checkAccess('logistics'),
    // 提交OA流程-订单核实
    'oa-process-verify': checkAccess('verify'),
    // 提交OA流程-订单重发
    'oa-process-resend': checkAccess('resend'),

    // 查看OA流程
    'oa-process-list': checkAccess('oa-process-view'),
    // 查看OA流程-退费流程
    'oa-process-list-refund': checkAccess('refund-process'),
    // 查看OA流程-取消订单
    'oa-process-list-cancel': checkAccess('cancel-order-process'),
    // 查看OA流程-加急订单
    'oa-process-list-urgent': checkAccess('urgent-order-process'),
    // 查看OA流程-物流核实
    'oa-process-list-logistics': checkAccess('logistics-process'),
    // 查看OA流程-订单核实
    'oa-process-list-verify': checkAccess('verify-process'),
    // 查看OA流程-订单重发
    'oa-process-list-resend': checkAccess('resend-process'),

     // oa流程相关 - saas模块
     'saas-oa-process': checkAccess('saas-oa-process-submit'),
     'saas-oa-process-gradient': checkAccess('saas-ladder-coupons'),
     'saas-oa-process-refund': checkAccess('saas-other-refund'),
     'saas-oa-process-cancel': checkAccess('saas-cancel-order'),
     'saas-oa-process-logistics': checkAccess('saas-logistics'),

     'saas-oa-process-verify': checkAccess('saas-verify'),
     'saas-oa-process-resend': checkAccess('saas-resend'),

     'saas-oa-process-list': checkAccess('saas-oa-process-view'),
     'saas-oa-process-list-refund': checkAccess('saas-refund-process'),
     'saas-oa-process-list-operation': checkAccess('saas-operation-process'),
     'saas-oa-process-list-cancel': checkAccess('saas-cancel-order-process'),
     'saas-oa-process-list-urgent': checkAccess('saas-urgent-order-process'),
     'saas-oa-process-list-logistics': checkAccess('saas-logistics-process'),
     'saas-oa-process-list-verify': checkAccess('saas-verify-process'),
     'saas-oa-process-list-resend': checkAccess('saas-resend-process'),
    //  邮件工单-订单详情-查看
     'ticket-detail-view': checkAccess('GET:api/admin/email-ticket/order-center/detail/{orderId}'),
    //  邮件工单-订单详情-是否展示梯度优惠
     'ladder-coupon': checkAccess('ladder-coupon'),
    //  邮件工单-订单详情-是否展示作废商品
    'scrapped-goods': checkAccess('scrapped-goods'),
    //  邮件工单-订单详情-是否展示库存/在途/未发货
    'stock': checkAccess('stock'),

    //  saas工单-订单详情-查看
    'saas-ticket-detail-view': checkAccess('GET:api/admin/saas-dispute/order-center/detail/{orderId}'),
    //  saas工单-订单详情-是否展示梯度优惠
    'saas-ladder-coupon': checkAccess('saas-ladder-coupon'),
    //  saas工单-订单详情-是否展示作废商品
    'saas-scrapped-goods': checkAccess('saas-scrapped-goods'),
    //  saas工单-订单详情-是否展示库存/在途/未发货
    'saas-stock': checkAccess('saas-stock'),

    // 工单-订单详情-订单标记
    'order-mark': checkAccess('order-mark'),
    //  saas工单-订单详情-订单标记
    'saas-order-mark': checkAccess('saas-order-mark'),

    // 2026/5/9 新增 筛选灰度权限
    'care-email-system-type': checkGreyAccess('CARE_EMAIL_SYSTEM_TYPE'),

    // 客户中心
    'customer-center': checkAccess('customer-center'),
  };

  return permissions;
}
